import { Task, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { ConnectionSummary, SDKRequestFor } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { Routing, SdkClient } from '~/effects'

type Context = {
  organizationId: string
  organizationSlug: string
  taxYear: number
}

export type ProConnectImportState =
  | (Context & { status: 'loadingConnection' })
  | (Context & {
      status: 'authorizeConnection'
      connectionStatus: 'idle' | 'redirecting'
    })
  | (Context & { status: 'acquiringReturns'; connection: ConnectionSummary })

const loadConnection = Task.define((organizationId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* sdk.send({
      type: 'integrations/getProConnectConnection',
      organizationId
    } satisfies SDKRequestFor<'integrations/getProConnectConnection'>)

    return {
      type: 'connectionLoaded',
      connection: result.connection ?? null
    } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

const beginConnection = Task.define((organizationId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const routing = yield* Routing
    const location = yield* routing.currentLocation
    const authorization = yield* sdk.send({
      type: 'integrations/beginProConnectConnection',
      organizationId,
      postConnectionRedirectUrl: location.origin + location.pathname
    } satisfies SDKRequestFor<'integrations/beginProConnectConnection'>)

    yield* routing.redirectTo(authorization.authorizationUrl)
    return null
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

const acquireReturns = Task.define(
  (organizationId: string, organizationSlug: string, connectionId: string, taxYear: number) =>
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const result = yield* sdk.send({
        type: 'integrations/importProConnectTaxReturns',
        connectionId,
        taxYear
      } satisfies SDKRequestFor<'integrations/importProConnectTaxReturns'>)

      const routing = yield* Routing
      yield* routing.navigate({
        name: 'run_import',
        organizationSlug,
        importId: result.id
      })

      return null
      // No failure has a product response yet, so an unexpected one is a defect.
    }).pipe(Effect.orDie),
  { key: (organizationId) => organizationId }
)

export type ProConnectImportMessage =
  | TaskMessage<typeof loadConnection>
  | TaskMessage<typeof acquireReturns>
  | { type: 'connectRequested' }

export const proConnectImportModule = defModule<{
  state: ProConnectImportState
  message: ProConnectImportMessage
  init: Context
}>({
  init: initialProConnectImportState,
  tasks: (state) =>
    match(state)
      .with({ status: 'loadingConnection' }, (state) => [loadConnection(state.organizationId)])
      .with({ status: 'authorizeConnection', connectionStatus: 'redirecting' }, (state) => [
        beginConnection(state.organizationId)
      ])
      .with({ status: 'acquiringReturns' }, (state) => [
        acquireReturns(
          state.organizationId,
          state.organizationSlug,
          state.connection.id,
          state.taxYear
        )
      ])
      .otherwise(() => []),
  update: updateProConnectImport
})

export function initialProConnectImportState({
  organizationId,
  organizationSlug,
  taxYear
}: Context): ProConnectImportState {
  return {
    status: 'loadingConnection',
    organizationId,
    organizationSlug,
    taxYear
  }
}

function updateProConnectImport(
  state: ProConnectImportState,
  message: ProConnectImportMessage
): UpdateResult<ProConnectImportState, ProConnectImportMessage> {
  return match({ state, message })
    .returnType<UpdateResult<ProConnectImportState, ProConnectImportMessage>>()
    .with(
      { state: { status: 'loadingConnection' }, message: { type: 'connectionLoaded' } },
      ({ state, message }) => {
        if (message.connection) {
          return { ...state, status: 'acquiringReturns', connection: message.connection }
        }

        return {
          ...state,
          status: 'authorizeConnection',
          connectionStatus: 'idle'
        }
      }
    )
    .with(
      {
        state: { status: 'authorizeConnection', connectionStatus: 'idle' },
        message: { type: 'connectRequested' }
      },
      ({ state }) => ({
        ...state,
        connectionStatus: 'redirecting'
      })
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function unexpectedMessage(state: ProConnectImportState, message: ProConnectImportMessage): never {
  throw new Error(`Unexpected ProConnect import message ${message.type} while ${state.status}`)
}
